<template>
  <div class="patient-tier-panel card">
    <div class="card-body">
      <h6 class="mb-3"><i class="fas fa-crown mr-1" aria-hidden="true"></i> ระดับลูกค้า</h6>

      <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

      <div v-if="isLoading" class="text-center text-muted py-3">
        <b-spinner small class="mr-2" /> กำลังโหลดข้อมูลระดับลูกค้า...
      </div>

      <template v-else>
        <div class="patient-tier-panel__summary mb-3">
          <p class="mb-1">
            <strong>ระดับปัจจุบัน:</strong>
            {{ tier ? tier.name : "ยังไม่มีระดับ" }}
            <!--
              GRACE still means the patient HOLDS this tier and its discount
              right now — the grace period is the window during which they
              can top up spend before a downgrade, not a "your benefit is
              gone" state. Say so explicitly here (not just via TierBadge's
              color) since this line is read carefully at the counter, not
              glanced at like the summary badge.
            -->
            <small v-if="state && state.status === 'GRACE'" class="text-info d-block">
              อยู่ในช่วงต่อสิทธิ์ — สิทธิ์และส่วนลดของระดับนี้ยังคงมีผลถึง {{ fmtDate(state.graceUntilDt) }}
            </small>
            <small v-if="state && state.isOverridden" class="text-muted d-block">
              (ระดับนี้ถูกล็อกไว้โดยพนักงาน จะไม่ถูกคำนวณใหม่อัตโนมัติจนกว่าจะล้างการล็อก)
            </small>
          </p>
          <p class="mb-1"><strong>ยอดสะสมที่นับได้:</strong> {{ fmtBaht(qualifiedAmount) }}</p>
          <p class="mb-0">
            <strong>ยอดที่ต้องเพิ่มเพื่อขึ้นระดับถัดไป:</strong>
            <span v-if="nextTierInfo">
              {{ fmtBaht(nextTierInfo.amountToNext) }}
              <template v-if="nextTierInfo.tier"> (ระดับ {{ nextTierInfo.tier.name }})</template>
            </span>
            <span v-else class="text-muted">อยู่ในระดับสูงสุดที่เปิดใช้งานอยู่แล้ว</span>
          </p>
        </div>

        <div v-if="canRecompute" class="mb-3">
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="isRecomputing"
            @click="doRecompute"
          >
            <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': isRecomputing }" aria-hidden="true"></i>
            {{ isRecomputing ? "กำลังคำนวณ..." : "คำนวณระดับใหม่" }}
          </b-button>
        </div>

        <!--
          The history IS the point of this panel — it answers "ทำไมฉันอยู่
          ระดับนี้" at the counter, so it renders even when there is no
          current tier or no controls the viewer can use.
        -->
        <div class="patient-tier-panel__history mb-3">
          <h6 class="mb-2"><i class="fas fa-history mr-1" aria-hidden="true"></i> ประวัติการเปลี่ยนระดับ</h6>
          <b-table
            :items="events"
            :fields="eventFields"
            small
            responsive
            show-empty
            empty-text="ยังไม่มีประวัติการเปลี่ยนระดับ"
          >
            <template #cell(eventType)="{ value }">{{ eventTypeLabel(value) }}</template>
            <template #cell(fromTierId)="{ value }">{{ tierNameFor(value) }}</template>
            <template #cell(toTierId)="{ value }">{{ tierNameFor(value) }}</template>
            <template #cell(qualifiedAmount)="{ value }">{{ fmtBaht(value) }}</template>
            <template #cell(creationDt)="{ value }">{{ fmtDate(value) }}</template>
          </b-table>
        </div>

        <div v-if="canOverride" class="patient-tier-panel__override">
          <h6 class="mb-2"><i class="fas fa-lock mr-1" aria-hidden="true"></i> ล็อกระดับด้วยตนเอง</h6>
          <b-form @submit.prevent="submitOverride">
            <b-form-group label="ระดับที่ต้องการล็อก" label-cols-sm="4" label-size="sm" class="mb-2">
              <b-form-select v-model="overrideForm.tierId" :options="overrideTierOptions" size="sm" />
            </b-form-group>
            <b-form-group label="หมายเหตุ" label-cols-sm="4" label-size="sm" class="mb-2">
              <b-form-textarea v-model="overrideForm.note" size="sm" rows="2" />
            </b-form-group>
            <b-alert :show="!!overrideErrorMessage" variant="danger">{{ overrideErrorMessage }}</b-alert>
            <div class="d-flex justify-content-end">
              <b-button type="submit" size="sm" variant="primary" :disabled="isSavingOverride">
                {{ isSavingOverride ? "กำลังบันทึก..." : "บันทึกการล็อกระดับ" }}
              </b-button>
            </div>
          </b-form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { PERMISSIONS } from "@/constants/roles";
import { tierErrorMessage } from "@/utils/tierErrors";
import { tierEventTypeLabel, computeNextTierInfo, tierNameFor as resolveTierName } from "@/utils/tierDisplay";

export default {
  name: "PatientTierPanel",
  props: {
    patient: { type: Object, required: true },
  },
  data() {
    return {
      isLoading: false,
      isRecomputing: false,
      isSavingOverride: false,
      errorMessage: "",
      overrideErrorMessage: "",
      // Local copies of the fetch, NOT read from the moduleTier getters.
      // moduleTier's `patientTier`/`tierList` state are single shared slots
      // (see stores/tier.js's header comment) — if this panel and
      // TierBadge/PatientProfile happen to be mounted at the same time for
      // different patients, reading through the getters would let one
      // overwrite what the other displays. Every action here resolves with
      // the data directly for exactly this reason; use that, not the
      // getter.
      patientTierData: null, // { state, tier, events }
      // Result of moduleTier's getPatientTierSummary (branch-scoped) — used
      // only for its amountToNextTier, the one summary figure getPatientTier
      // does not compute. See C1's fix note above canOverride/load().
      tierSummary: null,
      // includeInactive: true, so historical events referencing a tier that
      // has since been deactivated (but not deleted) still resolve to a
      // name instead of falling back to "#<id>".
      //
      // C1: stays [] for a pure branch member. listTiers is CLINIC-scoped
      // (authByClinicURL) and 403s any caller without a clinic_wide/
      // branch_subset scope — a 403 there is NOT catchable here, the global
      // axios interceptor (src/config/axios.js's 403/_permRetry path) acts
      // on it first and bounces the whole app to /clinic before this
      // component ever sees the rejection. load() only fetches it when
      // canOverride is true, which — per src/auth/permissions.js's
      // 'tier.override' grant list (C_LEVEL/ACCOUNTANT only) — already
      // implies a clinic-scoped viewer. Do NOT widen that gate or add
      // another listTiers/moduleTier "list" call anywhere in this
      // component's mount path; that reproduces the exact bug.
      allTiers: [],
      overrideForm: { tierId: null, note: "" },
      eventFields: [
        { key: "eventType", label: "ประเภท" },
        { key: "fromTierId", label: "จากระดับ" },
        { key: "toTierId", label: "ถึงระดับ" },
        { key: "qualifiedAmount", label: "ยอดสะสม ณ ขณะนั้น", class: "text-right" },
        { key: "creationDt", label: "วันที่" },
        { key: "note", label: "หมายเหตุ" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleUser", { can: "can", getBranchInfo: "getBranchInfo" }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    clinicId() {
      return this.getBranchInfo?.clinic?.id;
    },
    branchId() {
      return this.getBranchInfo?.id;
    },
    // tier.recompute / tier.override are branch-scoped permissions (the
    // per-patient endpoints authorize via authByClinicURLBranchURL — see
    // stores/tier.js's header comment), so both clinicId AND branchId go
    // into the ctx, matching how Sidebar.vue checks branch-scoped keys like
    // user.view.branch.
    canRecompute() {
      return this.can(PERMISSIONS.TIER_RECOMPUTE, { clinicId: this.clinicId, branchId: this.branchId });
    },
    canOverride() {
      return this.can(PERMISSIONS.TIER_OVERRIDE, { clinicId: this.clinicId, branchId: this.branchId });
    },
    state() {
      return this.patientTierData?.state || null;
    },
    tier() {
      return this.patientTierData?.tier || null;
    },
    events() {
      return this.patientTierData?.events || [];
    },
    // Already a Number — moduleTier's getPatientTier action coerces
    // state.qualifiedAmount via coerceStateMoney before it ever reaches a
    // component. Do not Number() it again here and do not treat it as a
    // string.
    qualifiedAmount() {
      return this.state?.qualifiedAmount ?? 0;
    },
    activeTiers() {
      return (this.allTiers || []).filter(t => t.isActive);
    },
    // See src/utils/tierDisplay.js for why this mirrors
    // getPatientTierPublic.js's own "next tier" algorithm (ranked by
    // thresholdAmount, not `rank`) rather than something invented here.
    //
    // Two data sources, picked in this order:
    // 1. allTiers, when we have it (canOverride viewers only — see C1's
    //    note on allTiers above) — gives BOTH the amount and the next
    //    tier's name, computed the same way it always was.
    // 2. Otherwise, the branch-scoped summary endpoint's amountToNextTier —
    //    the authoritative figure for every tier.view viewer, branch-only
    //    included, but with no tier object attached (getPatientTierPublic.js
    //    never sends one — it computes and keeps the next tier server-side).
    //    The template shows the amount alone in this case, no "(ระดับ …)".
    nextTierInfo() {
      if (this.allTiers.length) {
        return computeNextTierInfo(this.qualifiedAmount, this.allTiers);
      }
      const amount = this.tierSummary?.amountToNextTier;
      return amount != null ? { tier: null, amountToNext: Number(amount) } : null;
    },
    // Clear (tierId: null) is always offered; pinning to the patient's OWN
    // current tier is deliberately still offered too (re-locking after a
    // clear without picking a different tier is a legitimate action).
    // Same footgun TierForm.vue's batchOptions already guards against: if
    // the patient is currently pinned to a tier that has since been
    // deactivated, that tier must still appear here (even though it's
    // filtered out of activeTiers) or the select silently shows nothing
    // selected for a state that's actually still in effect.
    overrideTierOptions() {
      const options = [{ value: null, text: "ไม่ล็อกระดับ (คำนวณอัตโนมัติ)" }];
      const seen = new Set();
      for (const t of this.activeTiers) {
        options.push({ value: t.id, text: t.name });
        seen.add(t.id);
      }
      if (this.state?.isOverridden && this.state.tierId != null && !seen.has(this.state.tierId)) {
        const pinned = (this.allTiers || []).find(t => t.id === this.state.tierId);
        if (pinned) options.push({ value: pinned.id, text: `${pinned.name} (ปิดใช้งาน)` });
      }
      return options;
    },
  },
  methods: {
    ...mapActions("moduleTier", [
      "getPatientTier",
      "getPatientTierSummary",
      "recomputePatient",
      "overridePatient",
      "listTiers",
    ]),

    fmtBaht(value) {
      return `${Number(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} บาท`;
    },
    // DD/MM/YYYY, not moment's "th" locale (see TierBadge.vue's comment) —
    // an unambiguous numeric Gregorian year rather than one that reads like
    // a Buddhist-Era year and usually isn't.
    fmtDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "—";
    },
    eventTypeLabel(type) {
      return tierEventTypeLabel(type);
    },
    tierNameFor(tierId) {
      return resolveTierName(tierId, this.tier, this.allTiers);
    },

    async load() {
      if (!this.clinicUrl || !this.branchUrl || !this.patient?.id) return;
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const ctx = { clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, patientId: this.patient.id };
        const requests = [this.getPatientTier(ctx), this.getPatientTierSummary(ctx)];
        // See C1's note on `allTiers` above: only fetch the CLINIC-scoped
        // listTiers when the viewer can use what it's for (the override
        // select) — canOverride already implies a clinic-scoped role, so
        // this never 403s the way an unconditional call would for a
        // branch-only viewer.
        if (this.canOverride) {
          requests.push(
            this.listTiers({ clinicUrl: this.clinicUrl, includeInactive: true }).then(tiers => {
              this.allTiers = tiers;
            })
          );
        } else {
          this.allTiers = [];
        }
        const [tierData, summary] = await Promise.all(requests);
        this.patientTierData = tierData;
        this.tierSummary = summary;
        this.resetOverrideForm();
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "โหลดข้อมูลระดับลูกค้าไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },

    resetOverrideForm() {
      this.overrideForm = {
        tierId: this.state && this.state.isOverridden ? this.state.tierId : null,
        note: "",
      };
    },

    async doRecompute() {
      if (!this.clinicUrl || !this.branchUrl || !this.patient?.id) return;
      this.isRecomputing = true;
      this.errorMessage = "";
      try {
        await this.recomputePatient({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          patientId: this.patient.id,
        });
        // recomputePatient's response is a different, thinner shape than
        // getPatientTier's ({changed, tierId, status, qualifiedAmount,
        // events, pushes} vs {state, tier, events}) and its `events` entries
        // carry only eventType/voucherCodeId, none of the id/date/note this
        // panel's history table needs — so reload the full shape from
        // getPatientTier rather than trying to reconcile the two.
        await this.load();
      } catch (error) {
        this.errorMessage = tierErrorMessage(error) || "คำนวณระดับใหม่ไม่สำเร็จ";
      } finally {
        this.isRecomputing = false;
      }
    },

    async submitOverride() {
      if (!this.clinicUrl || !this.branchUrl || !this.patient?.id) return;
      this.isSavingOverride = true;
      this.overrideErrorMessage = "";
      try {
        await this.overridePatient({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          patientId: this.patient.id,
          // tierId MUST be sent explicitly, `null` included: the BE schema
          // marks it required, and moduleTier's overridePatient action
          // throws client-side if the key is missing rather than send a
          // request the BE would 400 (see stores/tier.js's header comment
          // on overridePatient). Building the payload as an object literal
          // here — never a conditional spread — guarantees the key is
          // always present, even when clearing (tierId: null).
          tierId: this.overrideForm.tierId,
          note: this.overrideForm.note || undefined,
        });
        // Always reload from getPatientTier after an override rather than
        // trust this call's own response — NOT an optional optimization to
        // drop. On a CLEAR specifically, overridePatient.js re-evaluates the
        // patient inside the same transaction, but the HTTP response it
        // sends back is built from a state object captured BEFORE that
        // re-evaluation ran, so `state.tierId`/`tier` in the response are
        // stale (documented backend residual — see overridePatient.js
        // around the `clearing` branch). The DB is correct; the response
        // is not. Reload unconditionally rather than special-casing
        // "clear vs pin" here, so the correctness guarantee doesn't depend
        // on someone keeping that branch logic in sync later.
        await this.load();
      } catch (error) {
        this.overrideErrorMessage = tierErrorMessage(error) || "บันทึกการล็อกระดับไม่สำเร็จ";
      } finally {
        this.isSavingOverride = false;
      }
    },
  },
  watch: {
    "patient.id": {
      immediate: true,
      handler(id) {
        if (id) this.load();
      },
    },
  },
};
</script>
